import React, { useMemo } from 'react';
export type GlyphShape = 'rise' | 'layers' | 'mark' | 'window' | 'link' | 'ripple';
type Props = {
  shape: GlyphShape;
  className?: string;
};

// Footer pixel language: white squares on near-black at stepped brightness.
// One extra top step so the core of each shape reads at this small size.
const STEPS = [0, 0.06, 0.12, 0.22, 0.36, 0.62];
const GRID = 9;
const circle = (u: number, v: number, cx: number, cy: number, r: number) => Math.hypot(u - cx, v - cy) <= r;
const box = (u: number, v: number, cx: number, cy: number, hw: number, hh: number) => Math.abs(u - cx) <= hw && Math.abs(v - cy) <= hh;

// Each shape returns brightness 0–1 for a cell centre (u, v ∈ [-1, 1])
const SHAPES: Record<GlyphShape, (u: number, v: number) => number> = {
  // Launch: a column rising, brightest at the top, with a dotted wake below
  rise: (u, v) => {
    if (Math.abs(u) < 0.2 && v > -0.85 && v < 0.35) return 1 - (v + 0.85) * 0.45;
    if (Math.abs(u) < 0.2 && v >= 0.35) return Math.round(v * 10 % 2) === 0 ? 0.25 : 0;
    return 0;
  },
  // Prototype: two offset screens, brighter where they overlap
  layers: (u, v) => {
    const a = box(u, v, -0.22, -0.22, 0.48, 0.48);
    const b = box(u, v, 0.22, 0.22, 0.48, 0.48);
    return a && b ? 1 : a ? 0.5 : b ? 0.3 : 0;
  },
  // Brand system: a mark — circle resting on a block
  mark: (u, v) => circle(u, v, 0, -0.38, 0.34) ? 0.95 : box(u, v, 0, 0.42, 0.34, 0.3) ? 0.45 : 0,
  // Website: a window with a bright title bar and quiet content lines
  window: (u, v) => {
    if (!box(u, v, 0, 0, 0.85, 0.62)) return 0;
    if (v < -0.4) return 1;
    const edge = Math.abs(u) > 0.7 || v > 0.48;
    if (edge) return 0.4;
    return Math.round((v + 1) * 4) % 2 === 0 && u < 0.3 ? 0.25 : 0.06;
  },
  // Partner: two circles, the overlap lit brightest
  link: (u, v) => {
    const a = circle(u, v, -0.33, 0, 0.55);
    const b = circle(u, v, 0.33, 0, 0.55);
    return a && b ? 1 : a ? 0.45 : b ? 0.3 : 0;
  },
  // Campaign: a centre point sending rings outward, dimming as they travel
  ripple: (u, v) => {
    const d = Math.max(Math.abs(u), Math.abs(v));
    if (d < 0.12) return 1;
    if (Math.abs(d - 0.45) < 0.12) return 0.5;
    if (Math.abs(d - 0.9) < 0.12) return 0.22;
    return 0;
  }
};

/** A 9×9 pixel illustration in the footer's pixel style, drawn as SVG so it stays crisp at any size. */
export function PixelGlyph({
  shape,
  className = ''
}: Props) {
  const cells = useMemo(() => {
    const fn = SHAPES[shape];
    const out: {
      x: number;
      y: number;
      o: number;
    }[] = [];
    for (let y = 0; y < GRID; y++) {
      for (let x = 0; x < GRID; x++) {
        const u = (x + 0.5) / GRID * 2 - 1;
        const v = (y + 0.5) / GRID * 2 - 1;
        const value = Math.max(0, Math.min(1, fn(u, v)));
        // Faint ambient dust so empty cells still read as a grid
        const dust = ((x * 7 + y * 13) % 11 === 0 ? 1 : 0) * 0.06;
        const o = value > 0 ? STEPS[Math.max(1, Math.round(value * (STEPS.length - 1)))] : dust;
        if (o > 0) out.push({
          x,
          y,
          o
        });
      }
    }
    return out;
  }, [shape]);
  return <svg viewBox={`0 0 ${GRID} ${GRID}`} className={`block h-full w-full bg-[#0f0f0f] ${className}`} aria-hidden="true" data-id="element-294" data-mp="3qhgnjdwvm3ns">
      {cells.map(c => <rect key={`${c.x}-${c.y}`} x={c.x + 0.04} y={c.y + 0.04} width={0.92} height={0.92} fill="#ffffff" opacity={c.o} data-id="element-295" data-mp="3bm0p6wp9jlll" />)}
    </svg>;
}